import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { 
  ArrowLeft, 
  ArrowDownRight, 
  ArrowUpRight, 
  TrendingUp,
  RefreshCw,
  CreditCard,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Transactions() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/transactions`);
      setTransactions(response.data);
    } catch (error) {
      // Error silenced
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'es' ? 'es-ES' : 'en-GB';
    return new Date(dateStr).toLocaleDateString(locale, {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <ArrowDownRight className="w-5 h-5 text-emerald-400" />;
      case 'withdrawal':
        return <ArrowUpRight className="w-5 h-5 text-red-400" />;
      case 'utility_payment':
        return <TrendingUp className="w-5 h-5 text-emerald-400" />;
      case 'reinvestment':
        return <RefreshCw className="w-5 h-5 text-blue-400" />;
      case 'credit':
        return <CreditCard className="w-5 h-5 text-emerald-400" />;
      case 'debit':
        return <CreditCard className="w-5 h-5 text-red-400" />;
      default:
        return <FileText className="w-5 h-5 text-slate-400" />;
    }
  };

  const getTransactionColor = (type) => {
    switch (type) {
      case 'deposit':
      case 'utility_payment':
      case 'credit':
      case 'reinvestment':
        return 'text-emerald-400';
      case 'withdrawal':
      case 'debit':
        return 'text-red-400';
      default:
        return 'text-white';
    }
  };

  const getTransactionSign = (type) => {
    switch (type) {
      case 'deposit':
      case 'utility_payment':
      case 'credit':
      case 'reinvestment':
        return '+';
      case 'withdrawal':
      case 'debit':
        return '-';
      default:
        return '';
    }
  };

  return (
    <div className="animate-fade-in" data-testid="transactions-page">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5 text-slate-400" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">{t('transactions.title')}</h1>
          <p className="text-slate-400 text-sm">{t('transactions.subtitle')}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-blue-500/30 border-t-blue-500 rounded-full animate-spin" />
        </div>
      ) : transactions.length > 0 ? (
        <div className="glass-card overflow-hidden">
          <div className="divide-y divide-white/5">
            {transactions.map((transaction) => (
              <div key={transaction.id} className="p-4 hover:bg-white/5 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      transaction.type === 'withdrawal' || transaction.type === 'debit'
                        ? 'bg-red-500/10'
                        : 'bg-emerald-500/10'
                    }`}>
                      {getTransactionIcon(transaction.type)}
                    </div>
                    <div>
                      <p className="text-white font-medium">{transaction.description}</p>
                      <p className="text-sm text-slate-400">
                        {formatDate(transaction.created_at)}
                      </p>
                    </div>
                  </div>
                  <span className={`text-lg font-semibold ${getTransactionColor(transaction.type)}`}>
                    {getTransactionSign(transaction.type)}{formatCurrency(transaction.amount)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="glass-card p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
            <FileText className="w-8 h-8 text-slate-500" />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">{t('transactions.noTransactions')}</h3>
          <p className="text-slate-400">{t('transactions.noTransactionsYet')}</p>
        </div>
      )}
    </div>
  );
}
